import { Fragment, useCallback, useMemo } from 'react';
import { Message } from 'components';
import { useRecoilValue } from 'recoil';
import { ComplexSettingFormState } from 'views/pipeline/store';
import { AddedActionsState, useOnboardingHook } from 'views/onboarding-flow';
import { PipelineMasterSteps } from 'global-stores';
import { ConfigActionKeys, ElementsToRemove } from 'views/pipeline/constants';
import { proofReadingKey } from 'views/pipelines/constants';
import { SuccessKybKeyRemoved } from '../../constants';
import { REGEX } from 'constant';
import {
	billedPrice,
	dedupeKycKeys,
	kycCheckPrice,
} from 'views/complex-onboarding-flow/pricing/kyc-pricing';
import { labelData } from 'views/complex-onboarding-flow';

export const ComplexPriceBreakDown = () => {
	const complexSettingForm = useRecoilValue(ComplexSettingFormState);
	const addedActions = useRecoilValue(AddedActionsState);
	const masterSteps = useRecoilValue(PipelineMasterSteps);

	const { isPricingHide } = useOnboardingHook();

	/* `addedActions` IS NOT THE WHOLE CATALOG, AND ON A FRESH BUILD IT IS BARELY
	   ANY OF IT.

	   It is seeded with `authentication` alone (complex-flow-layout) and filled
	   in properly only when an EXISTING pipeline is loaded for edit
	   (use-complex-layout `setAddedActions(actions)`). Everything else arrives
	   piecemeal, so an action the flow genuinely uses can be missing from it
	   while the customer is looking at the bill for that action.

	   AML is the case that bites: it is a checkbox ON the KYC card
	   (__simplici_kyc_aml_single_tile__), never dragged in as a step of its own,
	   so nothing ever pushes `amlVerification` into `addedActions` during a
	   fresh build. The canvas card prices it correctly because it reads the
	   master catalog directly — the same catalog the Actions List quotes from —
	   and that is what this falls back to, so the two screens cannot disagree
	   about what a step costs. */
	const fromMaster = useCallback(
		(actionKey: string) => {
			for (const step of masterSteps ?? []) {
				const hit = (step as any).actions?.find(
					(a: any) => a.key === actionKey
				);
				if (hit) return hit;
			}
			return undefined;
		},
		[masterSteps]
	);

	/* Prefer what the flow actually carries, fall back to the catalog. Never the
	   other way round: a per-flow price (liveliness flips `allId` between two
	   rates) has to win over the catalog's list price. */
	const actionOf = useCallback(
		(actionKey: string) =>
			addedActions?.find((el: any) => el.key === actionKey) ??
			fromMaster(actionKey),
		[addedActions, fromMaster]
	);

	// Function to remove objects with duplicate "key" property, specifically "formAction"
	const removeFormActionDuplicates = (array: unknown[]) => {
		const seenKeys = new Set();
		return array.filter((item: any) => {
			const key = item.key;
			if (key === 'formAction' && seenKeys.has(key)) {
				return false;
			}
			seenKeys.add(key);
			return true;
		});
	};

	//Shahbaaz: complex logic for getting action values for complex
	const complexAction = useMemo(() => {
		const orignalObj = Object.values(complexSettingForm ?? {})
		const foundSuccessScreenIndex = orignalObj.findIndex(
			(obj: any) => Object.prototype.hasOwnProperty.call(obj, 'appStoreUrl')
		);
		if(foundSuccessScreenIndex > -1){
			orignalObj.splice(foundSuccessScreenIndex, 1, {successScreen: true})	
		}
		// Shahbaaz:remove duplicate key form array
		const removalDuplicateResult = removeFormActionDuplicates(orignalObj);

		const copyObj = structuredClone(removalDuplicateResult);

		const newData = copyObj.map((item: any) => {
			if (item?.accreditationDocument) {
				delete item?.accreditationDocument;
			}
			return item;
		});

		//Shahbaaz: Filter only object properties which have some value in it
		const filterData = newData.filter(
			(item: any) => Object.keys(item).length > 0
		);
		//Shahbaaz: Filter only object properties
		const filteredArray = filterData.filter(
			(item: any) => typeof item === 'object'
		);
		const formActionIdx = filteredArray.findIndex(
			(item: any) => item?.key === ConfigActionKeys.FormAction
		);

		//Shahbaaz: Deleting form action object inside the filteredArray
		if (formActionIdx !== -1) {
			filteredArray.splice(formActionIdx, 1);
		}

		//Shahbaaz: convert all key the into array of string
		const keys = new Set();
		const arrayValue: any = [];

		filteredArray.forEach((obj: any) => {
			for (const key in obj) {
				if (obj[key] === true) {
					keys.add(key);
				}
				if (typeof obj[key] === 'string' && isNaN(Number(obj[key]))) {
					arrayValue.push(obj[key]);
				}
				if (key === ConfigActionKeys.AccreditationType) {
					const accDataee = filteredArray.filter(
						(el: { [x: string]: any }) => el[key]
					);
					accDataee?.forEach((item: any) => {
						arrayValue.push(item?.accreditationType?.value);
					});
				}
			}
		});
		//Shahbaaz: concat both keys into single array
		const result = [...Array.from(keys), ...arrayValue];

		//Shahbaaz: Remove gargabe value from result
		const uniqueResult = result.filter(
			item => !ElementsToRemove.includes(item)
		);

		//Shahbaaz: Initialize variables to keep track of the maximum true count and corresponding object
		let maxTrueCount = 0;
		let objectWithMaxTrue: any = null;

		//Shahbaaz: Iterate through the array and find the object with the most true values
		filteredArray.forEach(
			(obj: ArrayLike<unknown> | { [s: string]: unknown }) => {
				const trueCount = Object.values(obj).filter(
					value => value === true
				).length;
				if (trueCount > maxTrueCount) {
					maxTrueCount = trueCount;
					objectWithMaxTrue = obj;
				}
			}
		);

		filteredArray.filter((obj: { [x: string]: boolean }) => {
			for (const key in obj) {
				if (obj[key] === true) {
					uniqueResult.push(key);
				}
			}
		});

		//Shahbaaz: push max no. of true value inside the oject
		if (objectWithMaxTrue) {
			const keysObjectWithMaxTrue = Object.keys(objectWithMaxTrue).filter(
				key => objectWithMaxTrue[key]
			);
			keysObjectWithMaxTrue.forEach(el => {
				uniqueResult.push(el);
			});
		}

		if (formActionIdx !== -1) {
			uniqueResult.push(ConfigActionKeys.Form);
		}
		const finalResult = Array.from(new Set(uniqueResult));

		// Remove "Kyb" if both "Kyb" and "kyb_enhanced" are present
		if (finalResult.includes('Kyb') && finalResult.includes('kyb_enhanced')) {
			const index = finalResult.indexOf('Kyb');
			finalResult.splice(index, 1);
		}

		//Arun:Paras Remove KYB value from result becuse we need the configation only to show in price break down and removed success screen keys and url

		let completeData = finalResult.filter(
			item => !SuccessKybKeyRemoved.includes(item)
		);
		completeData =completeData.filter(item => !REGEX.isValidUrl.test(item) && REGEX.isValidKey.test(item));
		/* A PROVIDER SELECT IS NOT A LINE ITEM.
		   `kycProvider` was appearing in the customer's price breakdown at
		   $0.00 — it is the choice of WHICH provider performs the check, not a
		   charge. `constants.ts`'s ElementsToRemove already lists it, but that
		   filter runs earlier and the passes below it re-push every true-valued
		   key, so it came back. Re-applying the whole list here would also strip
		   `idv`/`ssn`/`addressVerification`, which those later passes restore on
		   purpose — so only the provider selects are named. */
		completeData = completeData.filter(
			item => !['kycProvider', 'kybProvider', 'amlProvider'].includes(item)
		);
		return completeData;
	}, [complexSettingForm]);

	/* __simplici_publish_quantity__
	   `complexAction` is a SET of feature keys, and a set is the wrong shape for
	   a bill: it cannot say "twice". Two Accreditation nodes both configured as
	   506(b) reduced to one '506b' row, so the Publish page quoted $4.00 for a
	   flow the canvas totals at $8.00 -- and the canvas is the one billing
	   agrees with. pipelines.api meters per NODE completion
	   (complex-sessions `updateAccreditationItemQuantity`, `updateKycItemQuantity`
	   -> `SubscriptionDetails.updateItemQuantity`, one usage increment each), so a
	   duplicated node genuinely bills twice.

	   `complexSettingForm` is keyed by canvas node id and the node's entry is
	   dropped when it is removed (custom-node `handleOnRemove`), so counting the
	   nodes that carry a key is counting the nodes that will bill for it. The
	   per-node derivation mirrors the passes above: a `true` toggle, a string
	   selection, or the accreditation level's `value`. A key none of the nodes
	   carry (`form`, `successScreen`, keys the later passes synthesise) is one. */
	const quantityOf = useMemo(() => {
		const counts: Record<string, number> = {};
		Object.values(complexSettingForm ?? {}).forEach((node: any) => {
			if (!node || typeof node !== 'object') return;
			const contributed = new Set<string>();
			for (const key in node) {
				const value = node[key];
				if (value === true) contributed.add(key);
				else if (typeof value === 'string' && isNaN(Number(value)))
					contributed.add(value);
				else if (
					key === ConfigActionKeys.AccreditationType &&
					typeof value?.value === 'string'
				)
					contributed.add(value.value);
			}
			contributed.forEach(key => {
				counts[key] = (counts[key] ?? 0) + 1;
			});
		});
		return (key: string) => Math.max(1, counts[key] ?? 0);
	}, [complexSettingForm]);

	const getTableLabel = (key: string) => {
		/* NAME THE THING THE CUSTOMER IS BEING CHARGED FOR.
		   Unmatched keys fell through to `default: return key`, so the breakdown
		   listed raw identifiers — "liveliness", "documentValidation" — to
		   someone about to be billed. Every one of them already has a product
		   name in the catalog the Actions List renders from, so read it there
		   first and keep the switch as the override for names that differ from
		   the catalog's. */
		const fromCatalog = addedActions?.reduce((found: string, act: any) => {
			if (found) return found;
			const meta = act?.metadata?.find((m: any) => m?.key === key);
			if (meta?.label) return meta.label;
			return act?.key === key && act?.label ? act.label : '';
		}, '');

		switch (key) {
			case 'esign':
				return 'Sign Agreement (esign)';
			case 'docusign':
				return 'Sign Agreement (DocuSign)';
			case 'successScreen':
				return 'Success Screen';
			// Shahbaaz:chnage key due to remove config for kyb
			// case 'Kyb':
			// 	return 'KYB';
			case 'payOut':
				return 'Pay out';
			case 'payIn':
				return 'Pay In';
			case 'addressVerification':
				return labelData['address'];
			/* __simplici_criminal_background_check__: named from the same map the
			   canvas tile reads, so the last screen before publishing calls the
			   check exactly what the tile called it. The `default` branch falls
			   back to the catalog and then to the RAW KEY, which is how a missing
			   case ends up printing "criminalBackgroundCheck" at a customer. */
			case 'criminalBackgroundCheck':
				return labelData['criminalBackgroundCheck'];
			case 'idv':
				return 'IDV - Document Verification';
			case 'idvSupportedDocument':
				return 'IDV Accepting FINCEN Supported Documents Only';
			case 'chequeFraud':
				return 'Check Defense';
			case 'ssn':
				return 'SSN';
			case '506b':
				return 'Accreditation (506b)';
			case '506c':
				return 'Verified Accreditation (506c)';
			case 'form':
				return 'Questionnaire';
			case 'elements':
				return 'KYB Documents';
			case 'amlVerification':
				return 'Anti money laundering(AML)';
			// Arun kumar : Enmus for Kyb configs
			case 'identity':
				return 'Individual or Business';
			/* Named as the canvas names the node it belongs to (DefaultNode's
			   __simplici_liveness_biometric_label__ and RenderBiometricBlock), so
			   the bill does not call the step something the customer never saw. */
			case 'faceEnrollment':
				return 'Liveness & Biometric';
			case 'palmEnrollment':
				return 'Palm Enrollment';
			case 'kyb_enhanced':
				return 'KYB Enhanced';
			case 'kyb_comprehensive':
				return 'KYB Comprehensive';
			case 'dun-brad-street-section':
				return 'Dun & Bradstreet';
			case 'kyb_physical_verification':
				return 'KYB Physical Verification';
			case 'civil-suits':
				return 'Civil Suits';
			case 'credit_check':
				return 'Credit Check';
			case 'background_check':
				return 'Background Check';
			case 'representative_kyc_aml_verification':
				return 'Representative KYC & AML Verification';
			case 'kyb-negative-filling':
				return 'KYB Negative Filling';
			case 'business_sanctions_ofac':
				return "Business Sanctions and OFAC"	
			case 'creditReport':
				return 'Credit Report';
			case 'proofReading':
				return proofReadingKey.DocumentReview;
			case 'kybSubsidiary':
				return 'KYB for Subsidiaries';
			case 'oneTime':
				 return "One Time AML";
			case 'dailyOnGoing':
				return "Daily on Going AML";	 	
			case 'amlFrequency': 
				return "AML Frequency";
			default:
				return fromCatalog || key;
		}
	};

	const getPrice = useCallback(
		(key: string) => {
			let metaPrice = 0;
			let metaData: any = [];
			switch (key) {
				case 'payIn':
				case 'payOut':
					metaPrice = actionOf(ConfigActionKeys.FundInvestmentVerification)
						?.price;

					return metaPrice;
				case 'docusign':
				case 'esign':
					metaData = actionOf(ConfigActionKeys.SignAgreementVerification)
						?.metadata?.[0]?.options;
					metaPrice = metaData?.find((el: any) => el.key === key)?.price;
					return metaPrice;
				case 'addressVerification':
				case 'idv':
				case 'ssn':
				/* THESE THREE WERE BILLING AT $0.00.
				   They are AllId metadata keys exactly like the four above, but
				   they had no case, so every one of them fell through to
				   `default: return 0` — and the Publish page quoted the customer
				   $0.00 for Global Government Issued ID Validation and Global
				   Data Validation, which the Actions List on step 1 prices at
				   $0.75 each from this same catalog. `liveliness` genuinely has
				   no metadata price and resolves to 0, which is what the Actions
				   List shows for it too. falls through */
				case 'liveliness':
				case 'documentValidation':
				case 'dataValidation':
				/* __simplici_criminal_background_check__: and it happened AGAIN, to
				   the row added right after that comment was written. A new AllId
				   metadata key has to be listed here or the Publish page quotes the
				   customer $0.00 for a check the tile beside it prices at $5.00 —
				   the number they agree to on the last screen before publishing.
				   That is the most expensive place in the product to be silently
				   wrong, and `default: return 0` makes being wrong the default. falls through */
				case 'criminalBackgroundCheck':
				case 'ssnVerification':
					/* PRICED BY THE SAME FUNCTION AS THE NODE CARD, from the LIVE
					   catalog — not the flow's saved snapshot. This used to read
					   `actionOf(AllId).metadata`, which prefers the snapshot taken when
					   the flow was saved: on a catalog that had since renamed these keys
					   the lookup missed and quoted $0.00, while the node card beside it
					   quoted something else again. Now both quote the BILLED figure
					   (the price book), find a renamed key under its older name, and
					   keep SSN free. The snapshot preference in `actionOf` was for the
					   `allId` ACTION price and still applies there. */
					return kycCheckPrice(
						fromMaster(ConfigActionKeys.AllId)?.metadata,
						key
					);
				case '506b':
				case '506c':
					metaData = actionOf(ConfigActionKeys.AccreditationVerify)
						?.metadata?.[0]?.options;
					metaPrice = metaData?.find((el: any) => el.value === key)?.price;
					return metaPrice;
				/* AML is priced on the ACTION, not in metadata — the same shape
				   as Kyb below. It had a LABEL case but no price case, so the
				   row read "Anti money laundering(AML)" against $0.00. */
				case 'amlVerification':
					// The billed figure, from the live catalog — as the node card does.
					return billedPrice(fromMaster(ConfigActionKeys.AmlVerification));
				// Shahbaaz: Adding key for showing kyb price
				case 'kybMatches':
				case 'Kyb':
					metaPrice = actionOf(ConfigActionKeys.KybVerification)?.price;
					return metaPrice;
				case 'form':
					return metaPrice;
				case 'elements':
					return metaPrice;
				case 'oneTime':
				case 'dailyOnGoing':
					metaData = actionOf(ConfigActionKeys.AmlVerification)
						?.metadata?.[0]?.options;
					metaPrice = metaData?.find((el: any) => el.key === key)?.price;
					return metaPrice;
				case 'identity':
					return metaPrice;
				// Arun kumar : Getting prices value for KYB
				case 'kyb_enhanced':
				case 'kyb_comprehensive':
				case 'kyb_physical_verification':
				case 'civil-suits':
				case 'credit_check':
				case 'representative_kyc_aml_verification':
				case 'background_check':
				case 'kyb-negative-filling':
				case 'dun-brad-street-section':
				case 'kybSubsidiary':
				case 'business_sanctions_ofac':
				/* __simplici_kyb_members_pep_sanctions__: same trap as the AllId
				   keys above -- a KYB metadata key with no case here falls to
				   `default: return 0` and the Publish page quotes $0.00 for a
				   check the tile beside it prices at $0.35 a person. falls through */
				case 'members_executives_pep_sanctions':
					metaData = actionOf(ConfigActionKeys.KybVerification)?.metadata;
					metaPrice = metaData?.find((el: any) => el.key === key)?.price;
					return metaPrice;

				default:
					return 0;
			}
		},
		[actionOf, fromMaster]
	);

	
	const usd = useMemo(
		() => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }),
		[]
	);

	/* One row per feature, with the quantity and line total spelled out, and
	   the sum at the foot -- the figure that has to equal the canvas Flow total. */
	const lineItems = useMemo(
		() =>
			/* One line per KYC check: a node configured against an older catalog
			   carries both names for a check (`dataValidation` and
			   `addressVerification`), and they now price the same row, so without
			   this the one check would be billed twice. */
			dedupeKycKeys(complexAction ?? []).map(item => {
				const quantity = quantityOf(item);
				const unitPrice = Number(getPrice(item) ?? 0) || 0;
				return { item, quantity, unitPrice, total: unitPrice * quantity };
			}),
		[complexAction, getPrice, quantityOf]
	);

	const flowTotal = useMemo(
		() => lineItems.reduce((sum, line) => sum + line.total, 0),
		[lineItems]
	);

	const getTableRow = useMemo(
		() => (
			<Fragment>
				{lineItems.map(({ item, quantity, unitPrice, total }) => (
					<tr key={item}>
						<>
							<td>
								<div className="details__table__label">
									{getTableLabel(item)}
								</div>
							</td>
							<td className="details__table__right">
								<div className="details__table__right__price details__table__qty">
									{quantity}
								</div>
							</td>
							{isPricingHide && (
								<>
									<td className="details__table__right">
										<div className="details__table__right__price">
											{usd.format(unitPrice)}
											<span>{item === 'dailyOnGoing' ? "/Per year" : "/Verification"} </span>
										</div>
									</td>
									<td className="details__table__right">
										<div className="details__table__right__price">
											{usd.format(total)}
										</div>
									</td>
								</>
							)}
						</>
					</tr>
				))}
			</Fragment>
		),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[lineItems, isPricingHide, usd]
	);
	const publishOnboardingFlow = useMemo(
		() => (
			<div className="details">
				<div className="details__info">
					<div className="details__info__title">Item pricing</div>
					<div className="details__info__description">
						Pricing of every action in the flow.
					</div>
				</div>
				<Message
					text={
						'Note: We charge you verification fee even if verification results in rejection.'
					}
					varient="warning"
				/>
				<table className="react-table-responsive details__table">
					<thead className="react-table-responsive__head">
						<tr className="react-table-responsive__head-row">
							<th className="table__head-data details__table__header-label">
								Action
							</th>
							<th className="table__head-data details__table__header-label details__table__right">
								Qty
							</th>
							{isPricingHide && (
								<>
									<th className="table__head-data details__table__header-label details__table__right">
										Unit price
									</th>
									<th className="table__head-data details__table__header-label details__table__right">
										Total
									</th>
								</>
							)}
						</tr>
					</thead>
					<tbody className="react-table-responsive__body">{getTableRow}</tbody>
					{isPricingHide && (
						<tfoot>
							<tr>
								{/* The total spans the Unit price AND Total columns. Squeezed
								    into the Total column alone (colSpan 3 + 1) it was cut to
								    "$1.42 /applica…" — that column is sized for a bare figure,
								    and this one carries its unit. Still four columns in all. */}
								<td colSpan={2}>
									<div className="details__table__total_label">Flow total</div>
								</td>
								<td className="details__table__right" colSpan={2}>
									<div className="details__table__total_price">
										{usd.format(flowTotal)}
										<span> /applicant</span>
									</div>
								</td>
							</tr>
						</tfoot>
					)}
				</table>
			</div>
		),
		[getTableRow, isPricingHide, flowTotal, usd]
	);

	return <>{publishOnboardingFlow}</>;
};
